/**
 * PodcastFilters - Search and filter controls for podcast list
 *
 * Single Responsibility: Renders search input and filter chips (tags only).
 * Does NOT handle data fetching or list rendering.
 */
package com.castly.admin.ui.podcasts

import androidx.compose.runtime.*
import com.castly.admin.ui.components.DropdownMenuItem
import com.castly.admin.ui.components.IconDropdownButton
import com.castly.admin.ui.icons.IconPlus
import com.castly.admin.ui.icons.IconRss
import com.castly.admin.ui.icons.IconUpload
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.*
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun PodcastFilters(
    searchQuery: String,
    tags: List<TagWithCountDto>,
    selectedTags: MutableSet<String>,
    onSearchChange: (String) -> Unit,
    onFilterTagsChange: (MutableSet<String>) -> Unit,
    onCreate: () -> Unit,
    onImportRss: () -> Unit,
    onBulkImportRss: () -> Unit
) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")

    // Tag visibility state
    var showAllTags by remember { mutableStateOf(false) }

    // Sort tags by podcast count (descending)
    val sortedTags = remember(tags) {
        tags.sortedByDescending { it.podcastCount }
    }

    // Show only first 7 tags unless "See More" is clicked
    val visibleTags = if (showAllTags) sortedTags else sortedTags.take(7)
    val hasMoreTags = sortedTags.size > 7

    // Tag filter chips with enhanced UX
    if (tags.isNotEmpty()) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.sm)
                marginBottom(Spacing.lg)
                property("flex-wrap", "wrap")
                alignItems(AlignItems.Center)
            }
        }) {
            // All tags filter
            Button({
                onClick {
                    val newTags = mutableSetOf<String>()
                    onFilterTagsChange(newTags)
                }
                style {
                    padding(6.px, 14.px)
                    backgroundColor(if (selectedTags.isEmpty()) Color(colors.primary) else Color(colors.surfaceHover))
                    color(if (selectedTags.isEmpty()) Color("#ffffff") else Color(colors.text))
                    border(1.px, LineStyle.Solid, if (selectedTags.isEmpty()) Color(colors.primary) else Color(colors.border))
                    borderRadius(16.px)
                    cursor("pointer")
                    fontSize(13.px)
                    fontWeight(if (selectedTags.isEmpty()) "600" else "500")
                    property("transition", "all 0.2s")
                }
            }) { Text("All Tags") }

            // Tag chips with integrated +/- controls
            visibleTags.forEach { tag ->
                val isSelected = selectedTags.contains(tag.id)
                val hasMultipleSelections = selectedTags.size > 1
                val hasOtherSelections = selectedTags.isNotEmpty() && !isSelected
                Button({
                    onClick {
                        val newTags = mutableSetOf<String>()
                        if (!isSelected) newTags.add(tag.id)
                        onFilterTagsChange(newTags)
                    }
                    style {
                        padding(6.px, 14.px)
                        backgroundColor(if (isSelected) Color(colors.primary) else Color(colors.surfaceHover))
                        color(if (isSelected) Color("#ffffff") else Color(colors.text))
                        border(1.px, LineStyle.Solid, if (isSelected) Color(colors.primary) else Color(colors.border))
                        borderRadius(16.px)
                        cursor("pointer")
                        fontSize(13.px)
                        fontWeight(if (isSelected) "600" else "500")
                        property("transition", "all 0.2s")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                    }
                }) {
                    Span { Text("${tag.name} (${tag.podcastCount})") }
                    // Show "+" to add tag when other tags selected
                    if (hasOtherSelections) {
                        Span({
                            onClick { event ->
                                event.stopPropagation()
                                val newTags = selectedTags.toMutableSet()
                                newTags.add(tag.id)
                                onFilterTagsChange(newTags)
                            }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                width(18.px)
                                height(18.px)
                                backgroundColor(Color(colors.success))
                                color(Color("#ffffff"))
                                borderRadius(9.px)
                                fontSize(Typography.base)
                                fontWeight("700")
                                cursor("pointer")
                                property("line-height", "1")
                            }
                        }) { Text("+") }
                    }

                    // Show "-" to remove tag when multiple selected
                    if (isSelected && hasMultipleSelections) {
                        Span({
                            onClick { event ->
                                event.stopPropagation()
                                val newTags = selectedTags.toMutableSet()
                                newTags.remove(tag.id)
                                onFilterTagsChange(newTags)
                            }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                width(18.px)
                                height(18.px)
                                backgroundColor(Color(colors.danger))
                                color(Color("#ffffff"))
                                borderRadius(9.px)
                                fontSize(Typography.base)
                                fontWeight("700")
                                cursor("pointer")
                                property("line-height", "1")
                            }
                        }) { Text("−") }
                    }
                }
            }

            // See More / Show Less button
            if (hasMoreTags) {
                Button({
                    onClick { showAllTags = !showAllTags }
                    style {
                        padding(6.px, 14.px)
                        backgroundColor(Color(colors.surfaceHover))
                        color(Color(colors.textMuted))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(16.px)
                        cursor("pointer")
                        fontSize(13.px)
                        fontWeight("500")
                        property("transition", "all 0.2s")
                    }
                }) {
                    Text(if (showAllTags) "Show Less" else "See More (${sortedTags.size - 7})")
                }
            }
        }
    }

    // Header with search and action buttons
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            marginBottom(Spacing.lg)
            gap(Spacing.lg)
        }
    }) {
        // Search input
        Input(InputType.Text) {
            attr("data-testid", "podcast-search-input")
            value(searchQuery)
            onInput { onSearchChange(it.value) }
            attr("placeholder", "Search podcasts...")
            style {
                padding(10.px, 14.px)
                fontSize(Typography.base)
                border(1.px, LineStyle.Solid, Color(colors.border))
                borderRadius(BorderRadius.md)
                flex("1")
                property("outline", "none")
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
                themeTransition()
            }
        }

        // Add new podcast dropdown
        if (canEdit) IconDropdownButton(
            tooltip = "New Podcast",
            items = listOf(
                DropdownMenuItem(
                    label = "Create Podcast",
                    description = "Add a new podcast manually",
                    icon = { IconPlus(size = 16) },
                    onClick = onCreate
                ),
                DropdownMenuItem(
                    label = "Import RSS",
                    description = "Import from RSS feed URL",
                    icon = { IconRss(size = 16) },
                    onClick = onImportRss
                ),
                DropdownMenuItem(
                    label = "Bulk Import RSS",
                    description = "Import multiple feeds at once",
                    icon = { IconUpload(size = 16) },
                    onClick = onBulkImportRss
                )
            )
        )
    }
}
